import { DateTime } from 'luxon';
// `Model` from its own module, not the `@/models` barrel — see the note in `index.ts`.
import { Model } from './model';
import { DateMode, FormFieldType, type Coordinates, FormFieldChoice,
  type FormModel, type NetworkLocationData, type CaseModel,
  type FormField} from '@/models';
import { getInitialMatrixData } from '@/components/data/entry/items/MatrixHelper';
import type { DrillData } from '@/components/forms/form_builder/FormFieldConfigHelper';
import { getLocalTimezoneDetails, getTimeFromSeconds, getUTCJSDate } from '@/utils';

export const OTHER_CHOICE_CODE = 'xxxx-OTHER_CHOICE-xxxx';

/**
 * Identifies the entry to load and submit. The entry store normally derives this from the current route, but hosts that
 * render the entry outside its own route (e.g. the pipeline's entry dialog) supply it explicitly instead.
 */
export interface EntryContext {
  formIdentifier: string;
  entryIdentifier?: string;
  caseIdentifier?: string;
  token?: string;
}

export interface NetworkEntries {
  entries: NetworkEntry[],
  entriesCount: number
}

export interface NetworkEntry {
  identifier: string;
  formIdentifier: string;
  formTitle?: string;
  caseIdentifier: string;
  creatorNames: string;
  responderName?: string | null;
  creatorAvatar?: string | null;
  responderAvatar?: string | null;
  lockedAt: string;
  createdAt: string;
  updatedAt: string;

  archivedAt?: string;
  items?: Record<string, string | number>
  completion?: EntryCompletion;
}

interface EntryCompletion {
  totalRequiredFields: number;
  answeredRequiredFields: number;
  unansweredFields: string[];
}

interface IEntry {
  identifier: string;
  formIdetifier: string;
  caseIdentifier?: string;
  formTitle?: string;
  caseItem?: CaseModel | undefined;
  creatorNames: string;
  creatorAvatar: string | null;
  lockedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;

  archivedAt?: Date;
  items?: Record<string, string | number>;
  completion?: EntryCompletion;
}

export class EntryModel extends Model {
  identifier: string;
  formIdentifier: string;
  caseIdentifier?: string;
  formTitle?: string;
  items?: Record<string, string | number>;
  caseItem?: CaseModel | undefined;
  creatorNames: string;
  creatorAvatar: string | null;
  lockedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  
  archivedAt?: Date;
  completion?: EntryCompletion;

  constructor(entry: IEntry) {
    super();
    this.identifier = entry.identifier;
    this.formIdentifier = entry.formIdetifier;
    this.caseIdentifier = entry.caseIdentifier;
    this.formTitle = entry.formTitle;
    this.caseItem = entry.caseItem;
    this.creatorNames = entry.creatorNames;
    this.creatorAvatar = entry.creatorAvatar;
    this.lockedAt = entry.lockedAt;
    this.createdAt = entry.createdAt;
    this.updatedAt = entry.updatedAt;
    this.archivedAt = entry.archivedAt;
    this.items = entry.items;
    this.completion = entry.completion;
  }

  static mapFromBackend(networkEntry: NetworkEntry, cases?: CaseModel[]) {
    return new EntryModel({
      identifier: networkEntry.identifier,
      formIdetifier: networkEntry.formIdentifier,
      caseIdentifier: networkEntry.caseIdentifier,
      formTitle: networkEntry.formTitle,
      caseItem: cases?.find(caseItem => caseItem.identifier === networkEntry.caseIdentifier) ?? undefined,
      items: networkEntry.items,
      creatorNames: networkEntry.creatorNames,
      creatorAvatar: networkEntry.creatorAvatar ?? networkEntry.responderAvatar ?? '',
      lockedAt: networkEntry.lockedAt ? DateTime.fromISO(networkEntry.lockedAt).toJSDate() : null,
      createdAt: DateTime.fromISO(networkEntry.createdAt).toJSDate(),
      updatedAt: DateTime.fromISO(networkEntry.updatedAt).toJSDate(),
      archivedAt: networkEntry.archivedAt ? DateTime.fromISO(networkEntry.archivedAt).toJSDate() : undefined,
      completion: networkEntry.completion
    });
  }

  static mapWithItems(networkEntry: NetworkEntry, form?: FormModel) {

    EntryModel.mapWithFormItems(form?.items, networkEntry.items);

    return new EntryModel({
      identifier: networkEntry.identifier,
      formIdetifier: networkEntry.formIdentifier,
      creatorNames: networkEntry.creatorNames ?? networkEntry.responderName,
      creatorAvatar: networkEntry.creatorAvatar ?? networkEntry.responderAvatar ?? '',
      lockedAt: networkEntry.lockedAt ? DateTime.fromISO(networkEntry.lockedAt).toJSDate() : null,
      createdAt: DateTime.fromISO(networkEntry.createdAt).toJSDate(),
      updatedAt: DateTime.fromISO(networkEntry.updatedAt).toJSDate(),
      archivedAt: networkEntry.archivedAt ? DateTime.fromISO(networkEntry.archivedAt).toJSDate() : undefined,
      completion: networkEntry.completion
    });
  }

  static mapWithFormItems(formItems?: FormField[], entryItems?: Record<string, string | number>) {
    formItems?.forEach(formField => {
      const value = entryItems![formField.identifier];

      
      switch (formField.type) {
        case FormFieldType.TEXT:
          if (value) {
            formField.value = `${value}`;
          } else {
            formField.value = '';
          }
          
          break;

        case FormFieldType.NUMBER:
          if (formField.options.range) {
            if (value) {
              formField.value = `${value}`;
            } else {
              formField.value = '';
            }
          } else {
            formField.numberValue = value ? parseFloat(`${value}`) : null;
          }
          break;

        case FormFieldType.CHOICE:
          if (formField.options.maximumChoicesCount == 1) {
            if (value) {
              formField.choiceValue = formField.options.choices?.find(choice => choice.value == value);
            }
            if (formField.options.allowOther) {
              formField.otherChoiceValue = {
                id: 99999999,
                label: 'Other',
                value: formField.choiceValue ? '-1' : value ? OTHER_CHOICE_CODE : '-1',
                otherValue: !formField.choiceValue ? `${value}` : ''
              };
            } 
            if (!formField.choiceValue) {
              formField.choiceValue = FormFieldChoice.createEmpty();
            }
          } else {
            const valueChoices = `${value}`?.split(',');
            if (valueChoices) {
              valueChoices.forEach(choiceValue => {
                for (const choice of formField.options.choices!) {
                  if (choice.value == choiceValue) {
                    choice.checked = true;
                    break;
                  }
                }
              });
            }

            for (const choice of formField.options.choices!) {
              if (choice.checked === undefined) {
                choice.checked = false;
              }
            }

            if (formField.options.allowOther) {
              let otherValue = '';
              if (valueChoices) {
                for (const valueChoice of valueChoices) {
                  let hasOtherValue = true;
                  for (const choice of formField.options.choices!) {
                    if (`${choice.value}` === valueChoice) {
                      hasOtherValue = false;
                      break;
                    }
                  }
                  if (hasOtherValue) {
                    otherValue = valueChoice;
                    break;
                  }
                }
              }

              formField.otherChoiceValue = {
                id: 99999999,
                label: 'Other',
                checked: otherValue !== '',
                otherValue: otherValue,
                value: otherValue ? OTHER_CHOICE_CODE : '-1'
              };
            }
          }
          break;

        case FormFieldType.DROPDOWN:
          if (formField.options.maximumChoicesCount == 1) {
            formField.choiceValue = formField.options.choices?.find(choice => choice.value == value);
          } else {
            const multipleChoices: FormFieldChoice[] = [];

            if (value === undefined) {
              break;
            }

            const valueChoices = `${value}`.split(',');
            if (valueChoices) {
              valueChoices.forEach(choiceValue => {
                for (const choice of formField.options.choices!) {
                  if (choice.value == choiceValue) {
                    multipleChoices.push(choice);
                    break;
                  }
                }
              });
            }

            formField.choiceValue = multipleChoices;
          }
          break;
      
        case FormFieldType.DATE:
          if (value) {
            if (formField.options.mode === DateMode.DATE_TIME) {
              /** When the absolute date option is selected, the timestamp is stored as the number of seconds since the Unix epoch (UTC seconds).
               * This means the time is fixed and should display the exact same moment, regardless of which timezone it’s viewed in.
               * Importantly, the display does NOT convert this time to the viewer’s local timezone. Instead, it displays the original collection
               * time along with the timezone information it was recorded in. For example, if the data was collected as ‘18 Sept 2025, 12:50 PM’,
               * it will always show ‘18 Sept 2025, 12:50 PM’ regardless of the viewer’s current timezone. */
              if (formField.options.absoluteDate) {
                formField.dateValue = getUTCJSDate(parseInt(value as string, 10));
              } else {
                formField.dateValue = DateTime.fromSeconds(parseInt(value as string)).toJSDate();
              }

              if (entryItems![`${formField.identifier}_data`]) {
                formField.timezoneRecorded = JSON.parse(entryItems![`${formField.identifier}_data`] as string);
                formField.overrideCalculatedValue = formField.timezoneRecorded?.overrideCalculatedValue;
              } else {
                formField.timezoneRecorded = getLocalTimezoneDetails();
              }
            } else if (formField.options.mode === DateMode.DATE) {
              formField.dateValue = getUTCJSDate(parseInt(value as string, 10));
            } else if (formField.options.mode === DateMode.TIME) {
              formField.dateValue = getTimeFromSeconds(parseInt(value as string, 10));
            }
          }
          break;
        
        case FormFieldType.LOCATION:
          if (value) {
            // eslint-disable-next-line no-case-declarations
            const locationObject: NetworkLocationData = JSON.parse(value as string);

            // eslint-disable-next-line no-case-declarations
            const coordinates: Coordinates = {
              latitude: locationObject.latitude,
              longitude: locationObject.longitude,
              accuracy: locationObject.accuracy
            };

            formField.locationValue = {
              coords: coordinates,
              timestamp: locationObject.timestamp
            };
          }
          break;

        case FormFieldType.SLIDER:
          formField.numberValue = value ? parseFloat(`${value}`) : undefined;
          break;

        case FormFieldType.SIGNATURE:
          if (value) {
            formField.signatureValue = JSON.parse(value as string);
          }
          break;

        case FormFieldType.FILE:
          if (value) {
            formField.fileIds = JSON.parse(value as string);
          }
          break;

        case FormFieldType.MATRIX:
          formField.matrixValues = value ? JSON.parse(value as string) : [];
          if (formField.matrixValues && formField.matrixValues.length > 0) {
            formField.hasExistingMatrixValue = true;
          }
          formField.matrixTableRowValues = getInitialMatrixData(formField);
          break;
        
        case FormFieldType.DRILLDOWN:

          // eslint-disable-next-line no-case-declarations
          const drillValues: DrillData[] = [];
          // eslint-disable-next-line no-case-declarations
          const drillLevels = (`${value}`)?.split(',');
          if (drillLevels) {
            // eslint-disable-next-line no-case-declarations
            let index = 0;
            for (const level of drillLevels) {
              const levelData = formField.options.drillLevels![index];
              if (levelData) {
                const foundLevel = levelData.data.find(data => data.label === level);
                if (foundLevel) {
                  drillValues.push(foundLevel);
                }
              }

              index++;
            }
          }
          formField.drillValue = drillValues;
          break;

        case FormFieldType.UNIT:
          if (value) {
            const unitValue = JSON.parse(value as string);
            formField.numberValue = unitValue.value;
            formField.value = unitValue.unit;

            if (unitValue.inequality) {
              formField.choiceValue = formField.options.choices?.find(choice => choice.value == unitValue.inequality);
            }
          } else {
            const defaultUnit = formField.options.units?.list.find(unitItem => unitItem.id === formField.options.units?.default);
            formField.value = defaultUnit?.unit;
          }
          break;

        default:
          formField.value = value as string;
          break;
      }
    });
  }
}

export interface EntryUpdateReason {
  reason: string;
  comment?: string;
  type: 'update' | 'review'
}

export interface PostEntry {
  items?: Record<string, string | number>,
  caseUri?: string | null;
  revisionDate: number;
  uri?: string;
  studyId?: string;
  entryToken?: string;
  reason?: EntryUpdateReason;
}

export interface UploadFile {
  filename: string;
  filetype: string;
  itemIdnetifier: string;
  size: number;
  progress?: number;
  file?: File;
}
